import { createContext, useState, useContext, useCallback, useRef, useEffect, useMemo, type ReactNode } from 'react';
import { Bug, ChevronDown, ChevronRight, Trash2, X, ArrowDown, Copy, Check } from 'lucide-react';

// ── Types ────────────────────────────────────────────────────────────────────

export interface DebugEvent {
  ts: number;
  stage: string;
  message: string;
  data?: Record<string, unknown>;
}

/** A turn's trace header, as `/api/chat/debug` sends it with each turn's log. */
export interface TurnTraceSummary {
  turnId: string;
  turnIndex?: number;
  cause: { kind: string; taskIds?: string[]; parentThreadId?: string; parentTurnId?: string };
  modelId?: string;
  state: string;
  startedAt: string;
  durationMs?: number;
  counts: { steps: number; toolCalls: number; events: number; links: number };
  tokens?: { input: number; output: number; cacheRead: number; cacheWrite: number; reasoning: number };
  error?: string;
  superseded?: boolean;
  phaseDurations?: Array<{ from: string; to: string; ms: number }>;
}

interface TraceStep {
  index: number;
  startedAt: number;
  firstChunkAt?: number;
  endedAt: number;
  inputTokens?: number;
  outputTokens?: number;
  finishReason?: string;
  tools?: string[];
}

interface TraceToolCall {
  toolCallId?: string;
  toolName: string;
  step: number;
  startedAt: number;
  durationMs: number;
  outcome: string;
}

interface TraceDetail {
  steps: TraceStep[];
  toolCalls: TraceToolCall[];
  links: Array<{ kind: string; id: string; toolCallId?: string; at: number }>;
}

interface DebugLogContextValue {
  entries: DebugEvent[][];
  turns: TurnTraceSummary[];
  threadId: string | null;
  refresh: () => void;
  clear: () => void;
  fetchJson: <T>(path: string) => Promise<T | null>;
}

const DebugLogContext = createContext<DebugLogContextValue>({
  entries: [],
  turns: [],
  threadId: null,
  refresh: () => {},
  clear: () => {},
  fetchJson: async () => null,
});

// ── Provider ─────────────────────────────────────────────────────────────────

interface DebugLogProviderProps {
  children: ReactNode;
  threadId: string | null;
  getToken: () => Promise<string | null>;
}

export function DebugLogProvider({ children, threadId, getToken }: DebugLogProviderProps) {
  const [entries, setEntries] = useState<DebugEvent[][]>([]);
  const [turns, setTurns] = useState<TurnTraceSummary[]>([]);
  const threadIdRef = useRef(threadId);
  threadIdRef.current = threadId;
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const fetchJson = useCallback(async <T,>(path: string): Promise<T | null> => {
    try {
      const streamApiUrl = import.meta.env.VITE_API_STREAM_URL || '';
      const token = await getTokenRef.current();
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const resp = await fetch(`${streamApiUrl}${path}`, { headers, credentials: streamApiUrl ? 'include' : 'same-origin' });
      if (!resp.ok) return null;
      const json = await resp.json();
      return json?.success ? (json as T) : null;
    } catch {
      return null;
    }
  }, []);

  const refresh = useCallback(async () => {
    const tid = threadIdRef.current;
    if (!tid) return;
    const json = await fetchJson<{ data: DebugEvent[][]; turns?: TurnTraceSummary[] }>(`/api/chat/debug/${encodeURIComponent(tid)}`);
    if (json && Array.isArray(json.data) && threadIdRef.current === tid) {
      setEntries(json.data);
      setTurns(Array.isArray(json.turns) ? json.turns : []);
    }
  }, [fetchJson]);

  const clear = useCallback(() => { setEntries([]); setTurns([]); }, []);

  useEffect(() => {
    setEntries([]);
    setTurns([]);
  }, [threadId]);

  const value = useMemo(() => ({ entries, turns, threadId, refresh, clear, fetchJson }), [entries, turns, threadId, refresh, clear, fetchJson]);

  return (
    <DebugLogContext.Provider value={value}>
      {children}
    </DebugLogContext.Provider>
  );
}

export function useDebugLog() {
  return useContext(DebugLogContext);
}

// ── UI Components ────────────────────────────────────────────────────────────

const STAGE_COLORS: Record<string, string> = {
  init: 'text-blue-400',
  sanitize: 'text-yellow-400',
  convert: 'text-cyan-400',
  compact: 'text-purple-400',
  prune: 'text-pink-400',
  budget: 'text-orange-400',
  summarize: 'text-amber-300',
  system: 'text-emerald-400',
  cache: 'text-teal-400',
  repair: 'text-rose-400',
  final: 'text-green-400',
  stream: 'text-sky-400',
  timings: 'text-indigo-300',
  error: 'text-red-400',
  abort: 'text-gray-500',
};

function formatTime(ts: number): string {
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return String(value);
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'string') return value.length > 120 ? value.slice(0, 117) + '...' : value;
  return JSON.stringify(value, null, 2);
}

function EventRow({ event }: { event: DebugEvent }) {
  const [expanded, setExpanded] = useState(false);
  const hasData = event.data && Object.keys(event.data).length > 0;
  const stageColor = STAGE_COLORS[event.stage] ?? 'text-gray-400';

  return (
    <div className="group hover:bg-white/[0.03] transition-colors">
      <div
        className={`flex items-start gap-2 px-3 py-1 font-mono text-[11px] leading-[18px] ${hasData ? 'cursor-pointer' : ''}`}
        onClick={() => hasData && setExpanded(!expanded)}
      >
        <span className="text-gray-500 shrink-0 w-[64px]">{formatTime(event.ts)}</span>
        <span className={`shrink-0 w-[72px] font-semibold ${stageColor}`}>
          {event.stage}
        </span>
        {hasData ? (
          <span className="shrink-0 w-3 mt-[2px] text-gray-600">
            {expanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          </span>
        ) : (
          <span className="shrink-0 w-3" />
        )}
        <span className="text-gray-300 flex-1 min-w-0 break-words">{event.message}</span>
      </div>
      {expanded && event.data && (
        <div className="ml-[148px] pr-3 pb-1.5">
          <div className="bg-black/30 rounded px-2 py-1.5 font-mono text-[10px] leading-[16px] space-y-0.5">
            {Object.entries(event.data).map(([key, val]) => (
              <div key={key} className="flex gap-2">
                <span className="text-gray-500 shrink-0">{key}:</span>
                <span className="text-gray-300 break-all whitespace-pre-wrap">{formatValue(val)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Turn header and timeline ─────────────────────────────────────────────────

function formatMs(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`;
  const m = Math.floor(ms / 60_000);
  return `${m}m${Math.round((ms % 60_000) / 1000)}s`;
}

/** What each stretch between two phase marks is called. */
const PHASE_LABELS: Record<string, string> = {
  'requested>processStarted': 'container start',
  'requested>wakeReady': 'wake wait',
  'requested>received': 'queued',
  'processStarted>wakeReady': 'wake wait',
  'processStarted>received': 'container setup',
  'wakeReady>received': 'container setup',
  'received>workspaceReady': 'workspace',
  'workspaceReady>summaryReady': 'summary wait',
  'workspaceReady>stateLoaded': 'thread state',
  'summaryReady>stateLoaded': 'thread state',
  'stateLoaded>registered': 'register',
  'received>registered': 'register',
  'registered>promptReady': 'prompt',
  'promptReady>firstChunk': 'first token',
  'promptReady>streamEnded': 'model + tools',
  'firstChunk>streamEnded': 'model + tools',
  'streamEnded>persistStarted': 'wrap up',
  'persistStarted>persisted': 'save',
  'persistStarted>ended': 'save',
  'persisted>ended': 'end',
  'ended>persisted': 'save (after end)',
  'streamEnded>ended': 'end',
};

function causeLabel(cause: TurnTraceSummary['cause']): string {
  switch (cause.kind) {
    case 'wake': return `wake (${cause.taskIds?.length ?? 0} task${cause.taskIds?.length === 1 ? '' : 's'})`;
    case 'subthread': return 'subthread';
    case 'answer': return 'answer';
    case 'headless': return 'headless';
    default: return 'user';
  }
}

function TurnHeader({ turn, index }: { turn?: TurnTraceSummary; index: number }) {
  const { threadId, fetchJson } = useDebugLog();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState<TraceDetail | null>(null);
  const [loading, setLoading] = useState(false);

  const toggle = useCallback(async () => {
    const next = !open;
    setOpen(next);
    if (next && !detail && turn && threadId) {
      setLoading(true);
      const json = await fetchJson<{ data: TraceDetail }>(
        `/api/chat/threads/${encodeURIComponent(threadId)}/traces/${encodeURIComponent(turn.turnId)}`,
      );
      setDetail(json?.data ?? null);
      setLoading(false);
    }
  }, [open, detail, turn, threadId, fetchJson]);

  if (!turn) {
    return (
      <div className="flex items-center gap-2 px-3 py-1 my-0.5">
        <div className="flex-1 h-px bg-gray-800" />
        <span className="text-[9px] text-gray-600 font-mono tabular-nums">request #{index + 1}</span>
        <div className="flex-1 h-px bg-gray-800" />
      </div>
    );
  }
  const phases = (turn.phaseDurations ?? []).filter((p) => p.ms >= 50);
  return (
    <div className="px-3 py-1 my-0.5 border-y border-gray-800/80 bg-white/[0.02] font-mono text-[10px]">
      <button onClick={toggle} className="flex items-center gap-1.5 w-full text-left text-gray-400 hover:text-gray-200">
        {open ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
        <span className="text-gray-200">turn {turn.turnIndex ?? '?'}</span>
        <span className="text-sky-400">{causeLabel(turn.cause)}</span>
        <span className={turn.state === 'finished' ? 'text-green-400' : turn.state === 'running' ? 'text-amber-300' : 'text-red-400'}>{turn.state}</span>
        {typeof turn.durationMs === 'number' && <span>{formatMs(turn.durationMs)}</span>}
        <span>{turn.counts.steps} steps · {turn.counts.toolCalls} tools</span>
        {turn.superseded && <span className="text-gray-600">(reverted)</span>}
        <span className="ml-auto text-gray-600">{new Date(turn.startedAt).toLocaleString('en-GB', { hour12: false })}</span>
      </button>
      {phases.length > 0 && (
        <div className="flex flex-wrap gap-x-2.5 gap-y-0.5 pl-[18px] text-gray-500">
          {phases.map((p) => (
            <span key={`${p.from}>${p.to}`}>{PHASE_LABELS[`${p.from}>${p.to}`] ?? `${p.from}→${p.to}`} <span className="text-gray-300">{formatMs(p.ms)}</span></span>
          ))}
        </div>
      )}
      {turn.error && <div className="pl-[18px] text-red-400 break-words">{turn.error}</div>}
      {open && (
        <div className="pl-[18px] pt-1">
          {loading && <div className="text-gray-600">loading…</div>}
          {!loading && !detail && <div className="text-gray-600">no trace</div>}
          {detail && <TraceTimeline detail={detail} />}
        </div>
      )}
    </div>
  );
}

/** Steps with the tool calls that ran in each: when, how long, how it ended. */
function TraceTimeline({ detail }: { detail: TraceDetail }) {
  const byStep = new Map<number, TraceToolCall[]>();
  for (const t of detail.toolCalls) {
    const list = byStep.get(t.step) ?? [];
    list.push(t);
    byStep.set(t.step, list);
  }
  const t0 = detail.steps[0]?.startedAt ?? detail.toolCalls[0]?.startedAt ?? 0;
  const linkByCall = new Map(detail.links.filter((l) => l.toolCallId).map((l) => [l.toolCallId!, l]));
  const at = (ms: number) => `+${formatMs(Math.max(0, ms - t0))}`;
  return (
    <div className="space-y-0.5">
      {detail.steps.map((s) => (
        <div key={s.index}>
          <div className="text-gray-300">
            <span className="text-gray-500 inline-block w-[64px]">{at(s.startedAt)}</span>
            step {s.index + 1} · {formatMs(s.endedAt - s.startedAt)}
            {s.firstChunkAt !== undefined && <span className="text-gray-500"> · first token {formatMs(s.firstChunkAt - s.startedAt)}</span>}
            {typeof s.inputTokens === 'number' && <span className="text-gray-500"> · {s.inputTokens.toLocaleString()} in / {(s.outputTokens ?? 0).toLocaleString()} out</span>}
            {s.finishReason && <span className="text-gray-600"> · {s.finishReason}</span>}
          </div>
          {(byStep.get(s.index) ?? []).map((t, i) => {
            const link = t.toolCallId ? linkByCall.get(t.toolCallId) : undefined;
            return (
              <div key={`${s.index}-${i}`} className="pl-4 text-gray-400">
                <span className="text-gray-600 inline-block w-[60px]">{at(t.startedAt)}</span>
                {t.toolName} <span className="text-gray-300">{formatMs(t.durationMs)}</span>
                {t.outcome !== 'ok' && <span className="text-red-400"> {t.outcome}</span>}
                {link && <span className="text-indigo-300"> → {link.kind} {link.id}</span>}
              </div>
            );
          })}
        </div>
      ))}
      {[...byStep.keys()].filter((k) => k >= detail.steps.length).flatMap((k) => byStep.get(k)!).map((t, i) => (
        <div key={`late-${i}`} className="pl-4 text-gray-400">
          <span className="text-gray-600 inline-block w-[60px]">{at(t.startedAt)}</span>
          {t.toolName} <span className="text-gray-300">{formatMs(t.durationMs)}</span> <span className="text-gray-600">(step unfinished)</span>
        </div>
      ))}
    </div>
  );
}

// ── Main Panel ───────────────────────────────────────────────────────────────

interface DebugPanelProps {
  onClose: () => void;
}

export function DebugPanel({ onClose }: DebugPanelProps) {
  const { entries, turns, refresh, clear } = useDebugLog();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const prevCountRef = useRef(0);

  const allEvents = useMemo(() => {
    const flat: Array<DebugEvent & { entryIdx: number }> = [];
    entries.forEach((batch, i) => {
      for (const ev of batch) {
        flat.push({ ...ev, entryIdx: i });
      }
    });
    return flat;
  }, [entries]);

  useEffect(() => {
    if (autoScroll && allEvents.length > prevCountRef.current) {
      scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
    prevCountRef.current = allEvents.length;
  }, [allEvents.length, autoScroll]);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    setAutoScroll(atBottom);
  }, []);

  const scrollToBottom = useCallback(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    setAutoScroll(true);
  }, []);

  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    const lines = allEvents.map((ev) => {
      const time = new Date(ev.ts).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
      const data = ev.data ? '\n' + Object.entries(ev.data).map(([k, v]) => `  ${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join('\n') : '';
      return `${time}  ${ev.stage.padEnd(10)}  ${ev.message}${data}`;
    });
    navigator.clipboard.writeText(lines.join('\n')).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [allEvents]);

  return (
    <div className="flex flex-col h-full bg-[#1a1a2e] text-gray-300">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-800 bg-[#16162a] shrink-0">
        <div className="flex items-center gap-2">
          <Bug className="w-3.5 h-3.5 text-green-400" />
          <span className="text-xs font-semibold text-gray-200 tracking-wide">Debug Console</span>
          <span className="text-[10px] text-gray-500 tabular-nums">
            {entries.length} {entries.length === 1 ? 'turn' : 'turns'} / {allEvents.length} events
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={refresh}
            className="px-2 py-0.5 rounded text-[10px] font-medium bg-green-900/40 text-green-400 hover:bg-green-900/60 transition-colors"
            title="Fetch latest debug data"
          >
            Refresh
          </button>
          <button
            onClick={handleCopy}
            className="p-1 rounded hover:bg-white/10 transition-colors"
            title="Copy all events to clipboard"
          >
            {copied
              ? <Check className="w-3.5 h-3.5 text-green-400" />
              : <Copy className="w-3.5 h-3.5 text-gray-400" />}
          </button>
          {!autoScroll && (
            <button
              onClick={scrollToBottom}
              className="p-1 rounded hover:bg-white/10 transition-colors"
              title="Scroll to bottom"
            >
              <ArrowDown className="w-3.5 h-3.5 text-gray-400" />
            </button>
          )}
          <button
            onClick={clear}
            className="p-1 rounded hover:bg-white/10 transition-colors"
            title="Clear debug log"
          >
            <Trash2 className="w-3.5 h-3.5 text-gray-400" />
          </button>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-white/10 transition-colors"
            title="Close debug panel"
          >
            <X className="w-3.5 h-3.5 text-gray-400" />
          </button>
        </div>
      </div>

      {/* Events */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto min-h-0 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent"
      >
        {allEvents.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-gray-600">
            <Bug className="w-6 h-6" />
            <span className="text-xs">Send a message then click Refresh to see pipeline debug events</span>
          </div>
        ) : (
          <div className="py-1">
            {allEvents.map((event, i) => {
              const prevEntry = i > 0 ? allEvents[i - 1].entryIdx : -1;
              // Every turn gets its header when the log carries traces; the
              // in-memory fallback has none, and only separators between turns.
              const showSeparator = event.entryIdx !== prevEntry && (i > 0 || turns.length > 0);
              return (
                <div key={`${event.entryIdx}-${i}`}>
                  {showSeparator && <TurnHeader turn={turns[event.entryIdx]} index={event.entryIdx} />}
                  <EventRow event={event} />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer with legend */}
      <div className="px-3 py-1.5 border-t border-gray-800 bg-[#16162a] shrink-0">
        <div className="flex flex-wrap gap-x-3 gap-y-0.5">
          {Object.entries(STAGE_COLORS).map(([stage, color]) => (
            <span key={stage} className={`text-[9px] font-mono ${color}`}>{stage}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
